// src/pages/home/homePage.js
// 1. Core libraries
import React, { useCallback, useEffect, useMemo, useReducer } from "react";
// 2. Third-party libraries
import { useNavigate } from "react-router-dom";
import { Card, Row, Col, Statistic, Skeleton } from "antd";
import { FaArrowUp, FaArrowDown, FaAngleDoubleRight, FaRupeeSign } from "react-icons/fa";
// 3. Application services
import axios from "../../services/axiosConfig";
import API_ENDPOINTS from "../../services/apiEndpoints";
import { getAuthToken } from "../../auth/auth";
import { getCurrentUser } from "aws-amplify/auth";
// 4. Application hooks
import useNotification from "../../hooks/useNotification";
// 5. Application components
import Layout from "../../components/layout/layout";
// 6. Styles
import "./homePage.css";

// Initial state for useReducer
const initialState = {
  counts: {
    users: 0,
    members: 0,
    leaders: 0,
    donations: 0
  },
  last30DayStats: {
    newUsers: 0,
    newMembers: 0,
    activeLeaders: 0,
    newDonations: 0
  },
  previous30DayStats: {
    newUsers: 0,
    newMembers: 0,
    activeLeaders: 0,
    newDonations: 0
  },
  upcomingEvents: [],
  recentPrayers: [],
  recentTestimonies: [],
  userName: "",
  loadingStates: {
    counts: true,
    events: true,
    prayers: true,
    testimonies: true,
    user: true
  },
  isAuthReady: false
};

// Reducer for state management
const dashboardReducer = (state, action) => {
  switch (action.type) {
    case "setAuthReady":
      return {
        ...state,
        isAuthReady: true
      };
    case "setUserName":
      return {
        ...state,
        userName: action.payload,
        loadingStates: { ...state.loadingStates, user: false }
      };
    case "setDashboardData":
      return {
        ...state,
        counts: action.payload.counts,
        last30DayStats: action.payload.last30DayStats,
        previous30DayStats: action.payload.previous30DayStats,
        loadingStates: { ...state.loadingStates, counts: false }
      };
    case "setEvents":
      return {
        ...state,
        upcomingEvents: action.payload,
        loadingStates: { ...state.loadingStates, events: false }
      };
    case "setPrayers":
      return {
        ...state,
        recentPrayers: action.payload,
        loadingStates: { ...state.loadingStates, prayers: false }
      };
    case "setTestimonies":
      return {
        ...state,
        recentTestimonies: action.payload,
        loadingStates: { ...state.loadingStates, testimonies: false }
      };
    case "setLoadingError":
      return {
        ...state,
        loadingStates: {
          counts: false,
          events: false,
          prayers: false,
          testimonies: false,
          user: false
        }
      };
    default:
      return state;
  }
};

function Dashboard() {
  const { notification, showNotification, hideNotification } = useNotification();
  const navigate = useNavigate();
  const [state, dispatch] = useReducer(dashboardReducer, initialState);

  const { counts, last30DayStats, previous30DayStats, upcomingEvents, recentPrayers, recentTestimonies, userName, loadingStates, isAuthReady } = state;

  // Helper function to check if date is within last N days
  const isWithinLastNDays = useCallback((dateString, days) => {
    if (!dateString) return false;

    const date = new Date(dateString);
    const now = new Date();
    const daysAgo = new Date(now);
    daysAgo.setDate(now.getDate() - days);

    return date >= daysAgo && date <= now;
  }, []);

  // Calculate trend direction and color
  const calculateTrend = useCallback((current, previous) => {
    if (previous === 0) {
      return {
        direction: current > 0 ? "up" : "neutral",
        color: current > 0 ? "hsl(145, 55%, 35%)" : "hsl(0, 0%, 50%)",
        icon: current > 0 ? FaArrowUp : FaArrowDown
      };
    }

    const isUp = current >= previous;
    return {
      direction: isUp ? "up" : "down",
      color: isUp ? "hsl(145, 55%, 35%)" : "hsl(0, 55%, 45%)",
      icon: isUp ? FaArrowUp : FaArrowDown
    };
  }, []);

  // Memoized trends calculation
  const trends = useMemo(
    () => ({
      users: calculateTrend(last30DayStats.newUsers, previous30DayStats.newUsers),
      members: calculateTrend(last30DayStats.newMembers, previous30DayStats.newMembers),
      leaders: calculateTrend(last30DayStats.activeLeaders, previous30DayStats.activeLeaders),
      donations: calculateTrend(last30DayStats.newDonations, previous30DayStats.newDonations)
    }),
    [last30DayStats, previous30DayStats, calculateTrend]
  );

  // Check if authentication is ready
  useEffect(() => {
    let isMounted = true;
    let retryCount = 0;
    const maxRetries = 5;

    const checkAuth = async () => {
      try {
        console.log("🔐 Checking authentication readiness...");
        await getCurrentUser();
        const token = await getAuthToken();

        if (!token) {
          throw new Error("Token not available");
        }

        if (isMounted) {
          console.log("✅ Authentication ready!");
          dispatch({ type: "setAuthReady" });
        }
      } catch (error) {
        console.log(`⏳ Auth not ready yet (attempt ${retryCount + 1}/${maxRetries})...`);

        if (retryCount < maxRetries) {
          retryCount++;
          setTimeout(checkAuth, 300 * retryCount); // Exponential backoff: 300ms, 600ms, 900ms...
        } else {
          console.error("❌ Authentication failed after max retries");
          if (isMounted) {
            showNotification("Authentication failed. Please try refreshing the page.", "error");
          }
        }
      }
    };

    checkAuth();

    return () => {
      isMounted = false;
    };
  }, [showNotification]);

  // Fetch user name (only after auth is ready)
  useEffect(() => {
    if (!isAuthReady) return;

    let isMounted = true;

    const fetchUserName = async () => {
      try {
        const currentUser = await getCurrentUser();
        const userId = currentUser.userId;

        const token = await getAuthToken();
        const response = await axios.get(`${API_ENDPOINTS.USERS}?status=signedup`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (isMounted) {
          const userData = response.data.users.find((user) => user.userId === userId);

          dispatch({
            type: "setUserName",
            payload: userData?.name || "User"
          });
        }
      } catch (error) {
        console.error("Error fetching user name:", error);
        if (isMounted) {
          dispatch({ type: "setUserName", payload: "User" });
        }
      }
    };

    fetchUserName();

    return () => {
      isMounted = false;
    };
  }, [isAuthReady]);

  // Fetch dashboard counts and statistics (only after auth is ready)
  useEffect(() => {
    if (!isAuthReady) return;

    let isMounted = true;

    const fetchDashboardData = async () => {
      try {
        const token = await getAuthToken();

        console.log("📡 Fetching dashboard data...");

        // Fetch users, members, events, prayers, and testimonies in parallel
        const [usersRes, membersRes, donationsRes, eventsRes, prayersRes, testimoniesRes] = await Promise.all([
          axios.get(`${API_ENDPOINTS.USERS}?status=signedup`, {
            headers: { Authorization: `Bearer ${token}` }
          }),
          axios.get(API_ENDPOINTS.MEMBERS, {
            headers: { Authorization: `Bearer ${token}` }
          }),
          // ✅ FIXED DONATIONS CALL
          (() => {
            const now = new Date();
            const toDate = now.toISOString();

            const fromDate = new Date(new Date().setFullYear(now.getFullYear() - 10)).toISOString();

            const url = `${API_ENDPOINTS.DONATIONS_ADMIN}` + 
              `?fromDate=${encodeURIComponent(fromDate)}` +
              `&toDate=${encodeURIComponent(toDate)}`;

            return axios.get(url, {
              headers: { Authorization: `Bearer ${token}` }
            });
          })(),
          axios.get(API_ENDPOINTS.CALENDAR, {
            headers: { Authorization: `Bearer ${token}` }
          }),
          axios.get(API_ENDPOINTS.PRAYER_REQUESTS_ADMIN, {
            headers: { Authorization: `Bearer ${token}` }
          }),
          axios.get(API_ENDPOINTS.TESTIMONIES, {
            headers: { Authorization: `Bearer ${token}` }
          })
        ]);

        if (!isMounted) return;

        // Process users
        const allUsers = usersRes.data.users || [];
        const totalUsers = allUsers.length;

        // New users in last 30 days (0-30 days ago)
        const newUsersLast30Days = allUsers.filter((user) => isWithinLastNDays(user.createdAt, 30)).length;

        // New users in previous 30 days (31-60 days ago)
        const newUsersPrevious30Days = allUsers.filter((user) => {
          if (!user.createdAt) return false;
          const date = new Date(user.createdAt);
          const now = new Date();
          const thirtyDaysAgo = new Date(now);
          const sixtyDaysAgo = new Date(now);
          thirtyDaysAgo.setDate(now.getDate() - 30);
          sixtyDaysAgo.setDate(now.getDate() - 60);
          return date >= sixtyDaysAgo && date < thirtyDaysAgo;
        }).length;

        // Process members
        const allMembers = membersRes.data.users || [];
        const totalMembers = allMembers.length;

        const newMembersLast30Days = allMembers.filter((member) => isWithinLastNDays(member.registrationDate, 30)).length;

        const newMembersPrevious30Days = allMembers.filter((member) => {
          if (!member.registrationDate) return false;
          const date = new Date(member.registrationDate);
          const now = new Date();
          const thirtyDaysAgo = new Date(now);
          const sixtyDaysAgo = new Date(now);
          thirtyDaysAgo.setDate(now.getDate() - 30);
          sixtyDaysAgo.setDate(now.getDate() - 60);
          return date >= sixtyDaysAgo && date < thirtyDaysAgo;
        }).length;

        // Process leaders
        const leaders = allUsers.filter((user) => {
          const role = user.role?.toLowerCase() || "";
          return role !== "" && role !== "user" && role !== "no role" && role !== "-";
        });
        const totalLeaders = leaders.length;

        const activeLeadersLast30Days = leaders.filter((leader) => isWithinLastNDays(leader.lastAdminLoginAt, 30)).length;

        const activeLeadersPrevious30Days = leaders.filter((leader) => {
          if (!leader.lastAdminLoginAt) return false;
          const date = new Date(leader.lastAdminLoginAt);
          const now = new Date();
          const thirtyDaysAgo = new Date(now);
          const sixtyDaysAgo = new Date(now);
          thirtyDaysAgo.setDate(now.getDate() - 30);
          sixtyDaysAgo.setDate(now.getDate() - 60);
          return date >= sixtyDaysAgo && date < thirtyDaysAgo;
        }).length;

        // ---------------- DONATIONS DATA ----------------
        const donationsData = Array.isArray(donationsRes.data) ? donationsRes.data : [];

        // Total donations (all time)
        const totalDonations = donationsData.reduce((sum, d) => sum + (d.amount || 0), 0);

        // Last 30 days
        const newDonationsLast30Days = donationsData.filter((d) => isWithinLastNDays(d.submittedAt, 30)).reduce((sum, d) => sum + (d.amount || 0), 0);

        // Previous 30 days
        const newDonationsPrevious30Days = donationsData
          .filter((d) => {
            if (!d.submittedAt) return false;

            const date = new Date(d.submittedAt);
            const now = new Date();

            const thirtyDaysAgo = new Date(now);
            const sixtyDaysAgo = new Date(now);

            thirtyDaysAgo.setDate(now.getDate() - 30);
            sixtyDaysAgo.setDate(now.getDate() - 60);

            return date >= sixtyDaysAgo && date < thirtyDaysAgo;
          })
          .reduce((sum, d) => sum + (d.amount || 0), 0);

        // Update dashboard data
        dispatch({
          type: "setDashboardData",
          payload: {
            counts: {
              users: totalUsers,
              members: totalMembers,
              leaders: totalLeaders,
              donations: totalDonations
            },
            last30DayStats: {
              newUsers: newUsersLast30Days,
              newMembers: newMembersLast30Days,
              activeLeaders: activeLeadersLast30Days,
              newDonations: newDonationsLast30Days
            },
            previous30DayStats: {
              newUsers: newUsersPrevious30Days,
              newMembers: newMembersPrevious30Days,
              activeLeaders: activeLeadersPrevious30Days,
              newDonations: newDonationsPrevious30Days
            }
          }
        });

        // Process upcoming events
        const eventsData = eventsRes.data?.events || [];
        const now = new Date();

        const upcoming = eventsData
          .filter((event) => {
            const eventDate = new Date(event.date.replace(/\//g, "-"));
            return eventDate >= now;
          })
          .sort((a, b) => {
            const dateA = new Date(a.date.replace(/\//g, "-"));
            const dateB = new Date(b.date.replace(/\//g, "-"));
            return dateA - dateB;
          })
          .slice(0, 3);

        dispatch({ type: "setEvents", payload: upcoming });

        // Process prayers
        const prayersData = prayersRes.data || [];

        const urgent = prayersData
          .filter((prayer) => {
            const isPublic = prayer.is_public === true || prayer.is_public === "true";
            const isApproved = prayer.status === "Approved";
            return isPublic && isApproved;
          })
          .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
          .slice(0, 3);

        dispatch({ type: "setPrayers", payload: urgent });

        // Process testimonies
        const testimoniesData = testimoniesRes.data || [];

        const recent = testimoniesData
          .filter((testimony) => {
            const isPublic = testimony.is_public === true || testimony.is_public === "true";
            const isApproved = testimony.status === "Approved";
            return isPublic && isApproved;
          })
          .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
          .slice(0, 3);

        dispatch({ type: "setTestimonies", payload: recent });

        console.log("✅ Dashboard data loaded successfully!");
      } catch (error) {
        console.error("❌ Error fetching dashboard data:", error);
        if (isMounted) {
          dispatch({ type: "setLoadingError" });
          showNotification("Failed to load dashboard data", "error");
        }
      }
    };

    fetchDashboardData();

    return () => {
      isMounted = false;
    };
  }, [isAuthReady, isWithinLastNDays, showNotification]);

  // Memoized helper functions
  const formatEventTime = useCallback((timings) => {
    if (!timings) return "Time TBD";
    const startTime = timings.split(" to ")[0];
    return startTime || "Time TBD";
  }, []);

  const formatEventDate = useCallback((dateString) => {
    if (!dateString) return "Date TBD";

    const date = new Date(dateString.replace(/\//g, "-"));
    const options = { month: "long", day: "numeric" };
    const formatted = date.toLocaleDateString("en-US", options);

    const day = date.getDate();
    let suffix = "th";
    if (day === 1 || day === 21 || day === 31) suffix = "st";
    else if (day === 2 || day === 22) suffix = "nd";
    else if (day === 3 || day === 23) suffix = "rd";

    return formatted.replace(/\d+/, `${day}${suffix}`);
  }, []);

  const truncateText = useCallback((text, maxLength = 50) => {
    if (!text) return "No description available";
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  }, []);

  // Memoized metric cards
  const metricCards = useMemo(
    () => [
      {
        title: "Total Users",
        value: counts.users,
        trend: trends.users,
        info: `${last30DayStats.newUsers} new in last 30 days`,
        redirect: () => navigate("/userList"),
        color: "hsl(210, 55%, 50%)"
      },
      {
        title: "Total Members",
        value: counts.members,
        trend: trends.members,
        info: `${last30DayStats.newMembers} new in last 30 days`,
        redirect: () => navigate("/memberList"),
        color: "hsl(350, 55%, 40%)"
      },
      {
        title: "Total Leaders",
        value: counts.leaders,
        trend: trends.leaders,
        info: `${last30DayStats.activeLeaders} active in last 30 days`,
        redirect: () => navigate("/leaderList"),
        color: "hsl(265, 50%, 60%)"
      },
      {
        title: "Total Donations",
        value: counts.donations.toLocaleString(),
        trend: trends.donations,
        info: `${last30DayStats.newDonations.toLocaleString()} in last 30 days`,
        suffix: <FaRupeeSign style={{ fontWeight: 100, fontSize: "18px" }} />,
        redirect: () => navigate("/donationList"),
        color: "hsl(35, 70%, 50%)"
      }
    ],
    [counts, trends, last30DayStats, navigate]
  );

  return (
    <Layout
      navTitle="Home"
      notificationProps={[
        {
          show: notification.show,
          message: notification.message,
          type: notification.type,
          onClose: hideNotification,
          duration: 4000
        }
      ]}
    >
      <div className="page-top-section">
        <h1 className="home-main-title">
          Welcome
          {loadingStates.user ? (
            <span className="home-loading-dots">
              <span>.</span>
              <span>.</span>
              <span>.</span>
            </span>
          ) : (
            ", " + userName + "!"
          )}
        </h1>
      </div>

      {/* Key Metrics */}
      <h2 className="home-section-title">Key Metrics</h2>
      <section className="home-section">
        <Row gutter={16}>
          {metricCards.map((metric, index) => {
            const TrendIcon = metric.trend.icon;
            return (
              <Col span={6} key={index}>
                <Card
                  className="home-row-card"
                  variant="borderless"
                  onClick={metric.redirect}
                  style={{
                    cursor: metric.redirect ? "pointer" : "default",
                    borderLeft: `5px solid ${metric.color}`
                  }}
                >
                  {loadingStates.counts ? (
                    <Skeleton active paragraph={{ rows: 2 }} />
                  ) : (
                    <>
                      <Statistic
                        title={metric.title}
                        value={metric.value}
                        valueStyle={{ color: metric.trend.color }}
                        prefix={<TrendIcon style={{ color: metric.trend.color }} />}
                        suffix={metric.suffix}
                      />
                      <span className="home-info">{metric.info}</span>
                    </>
                  )}
                </Card>
              </Col>
            );
          })}
        </Row>
      </section>

      {/* Upcoming Events */}
      <h2 className="home-section-title">Upcoming Events</h2>
      <section className="home-section" onClick={() => navigate("/eventList")} style={{ cursor: "pointer" }}>
        {loadingStates.events ? (
          <Row gutter={16}>
            {[1, 2, 3].map((_, index) => (
              <Col span={8} key={index}>
                <Card variant="borderless" className="home-row-card" style={{ borderLeft: "5px solid hsl(230, 50%, 50%)" }}>
                  <Skeleton active paragraph={{ rows: 1 }} />
                </Card>
              </Col>
            ))}
          </Row>
        ) : upcomingEvents.length === 0 ? (
          <Card variant="borderless" className="home-row-card" style={{ borderLeft: "5px solid hsl(230, 50%, 50%)" }}>
            <p>No upcoming events scheduled.</p>
          </Card>
        ) : (
          <Row gutter={16}>
            {upcomingEvents.map((event, index) => (
              <Col span={8} key={index}>
                <Card variant="borderless" className="home-row-card" style={{ borderLeft: "5px solid hsl(230, 50%, 50%)" }}>
                  <p>{event.title || "Untitled Event"}</p>
                  <span className="home-info">
                    {formatEventDate(event.date)}, {formatEventTime(event.timings)}
                  </span>
                </Card>
              </Col>
            ))}

            {upcomingEvents.length === 1 && (
              <>
                <Col span={8}>
                  <Card variant="borderless" className="home-row-card" style={{ borderLeft: "5px solid hsl(230, 50%, 50%)" }}>
                    <p>No additional events.</p>
                    <span className="home-info">—</span>
                  </Card>
                </Col>
                <Col span={8}>
                  <Card variant="borderless" className="home-row-card" style={{ borderLeft: "5px solid hsl(230, 50%, 50%)" }}>
                    <p>No additional events.</p>
                    <span className="home-info">—</span>
                  </Card>
                </Col>
              </>
            )}

            {upcomingEvents.length === 2 && (
              <Col span={8}>
                <Card variant="borderless" className="home-row-card" style={{ borderLeft: "5px solid hsl(230, 50%, 50%)" }}>
                  <p>No additional events.</p>
                  <span className="home-info">—</span>
                </Card>
              </Col>
            )}
          </Row>
        )}
      </section>

      {/* Prayer Points & Testimonies */}
      <section className="home-section">
        <Card variant="borderless" style={{ backgroundColor: "inherit", boxShadow: "none" }}>
          <Row gutter={16}>
            <Col span={12} onClick={() => navigate("/prayerList")} style={{ cursor: "pointer" }}>
              <h2 className="home-section-title">Recent Prayers</h2>
              <Card variant="borderless" className="home-row-card hrc" style={{ borderLeft: "5px solid hsl(145, 55%, 40%)" }}>
                {loadingStates.prayers ? (
                  <Skeleton active paragraph={{ rows: 3 }} />
                ) : recentPrayers.length === 0 ? (
                  <p style={{ padding: "20px", textAlign: "center" }}>No approved public prayers at this time.</p>
                ) : (
                  <ul className="home-list hl-one">
                    {recentPrayers.map((prayer, index) => (
                      <li key={index}>
                        <FaAngleDoubleRight className="hl-one-li" />
                        <span>
                          {prayer.name || "Anonymous"}: <i>{truncateText(prayer.description || prayer.title, 45)}</i>
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </Card>
            </Col>

            <Col span={12} onClick={() => navigate("/testimonyList")} style={{ cursor: "pointer" }}>
              <h2 className="home-section-title">Recent Testimonies</h2>
              <Card variant="borderless" className="home-row-card hrc" style={{ borderLeft: "5px solid hsl(50, 60%, 60%)" }}>
                {loadingStates.testimonies ? (
                  <Skeleton active paragraph={{ rows: 3 }} />
                ) : recentTestimonies.length === 0 ? (
                  <p style={{ padding: "20px", textAlign: "center" }}>No approved public testimonies at this time.</p>
                ) : (
                  <ul className="home-list hl-two">
                    {recentTestimonies.map((testimony, index) => (
                      <li key={index}>
                        <FaAngleDoubleRight className="hl-two-li" />
                        <span>
                          {testimony.name || "Anonymous"}: <i>{truncateText(testimony.description || testimony.title, 45)}</i>
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </Card>
            </Col>
          </Row>
        </Card>
      </section>
    </Layout>
  );
}

export default Dashboard;
